import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Wave 1.6 (v1) — Top-level error boundary.
 * Prevents a single render error from blanking the entire app
 * (previously any uncaught render error produced a white screen).
 * A global toast system and per-page migration are follow-ups.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /** Deploy-resilience: stale-tab chunk loads fail after a rebuild
   * (hashed filenames change). Detect and offer a one-click reload. */
  static isChunkLoadError(error: Error | null): boolean {
    if (!error) return false;
    const msg = `${error.name}: ${error.message}`;
    return (
      error.name === "ChunkLoadError" ||
      /Loading chunk \S+ failed|dynamically imported module|Importing a module script failed/i.test(msg)
    );
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Hook point for Wave 2 alerting / Wave 4 tracing (send to backend)
    console.error("Unhandled render error:", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (ErrorBoundary.isChunkLoadError(this.state.error)) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bgcolor="background.default"
            p={3}
          >
            <Paper sx={{ p: 4, maxWidth: 480, textAlign: "center" }}>
              <CloudDownloadIcon color="primary" sx={{ fontSize: 56, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                A new version is available
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                The application was updated while this tab was open. Reload to
                load the current version — your session is preserved.
              </Typography>
              <Button variant="contained" onClick={this.handleReload}>
                Reload now
              </Button>
            </Paper>
          </Box>
        );
      }
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="background.default"
          p={3}
        >
          <Paper sx={{ p: 4, maxWidth: 520, textAlign: "center" }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              An unexpected error occurred while rendering this page. The error has
              been logged. If this persists, contact your administrator with the
              details below.
            </Typography>
            {this.state.error && (
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  bgcolor: "action.hover",
                  p: 1.5,
                  borderRadius: 1,
                  overflow: "auto",
                  textAlign: "left",
                  maxHeight: 140,
                  mb: 2,
                  fontFamily: "monospace",
                }}
              >
                {this.state.error.message}
              </Typography>
            )}
            <Box display="flex" gap={1} justifyContent="center">
              <Button variant="outlined" onClick={this.handleGoHome}>
                Go to Dashboard
              </Button>
              <Button variant="contained" onClick={this.handleReload}>
                Reload Page
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
